import behaviourCsvUploadConstants from "@/constants/behaviourCsvUploadConstants.js"

/**
 * @param {string} uploadKind
 * @returns {typeof behaviourCsvUploadConstants.UPLOAD_DEFINITIONS[keyof typeof behaviourCsvUploadConstants.UPLOAD_DEFINITIONS] | null}
 */
export function getBehaviourCsvUploadDefinition(uploadKind) {
  return behaviourCsvUploadConstants.UPLOAD_DEFINITIONS?.[uploadKind] || null
}

/**
 * @param {string} uploadKind
 * @returns {boolean}
 */
export function isValidBehaviourCsvUploadKind(uploadKind) {
  return behaviourCsvUploadConstants.UPLOAD_KIND_VALUES.includes(uploadKind)
}

/**
 * @param {string} uploadKind
 * @param {Record<string, string>} terminology
 * @param {(key: string, values?: Record<string, string>) => string} t
 * @returns {string}
 */
export function resolveBehaviourCsvUploadModalTitle(
  uploadKind,
  terminology,
  t
) {
  const def = getBehaviourCsvUploadDefinition(uploadKind)
  if (!def?.titleI18nKey) return ""
  return t(def.titleI18nKey, terminology || {})
}

/**
 * @param {string} uploadKind
 * @param {Record<string, string>} terminology
 * @param {(key: string, values?: Record<string, string>) => string} t
 * @returns {string}
 */
export function resolveBehaviourCsvUploadFileLabel(uploadKind, terminology, t) {
  const def = getBehaviourCsvUploadDefinition(uploadKind)
  if (!def?.fileLabelI18nKey) return ""
  return t(def.fileLabelI18nKey, terminology || {})
}

/**
 * @param {File | null | undefined} file
 * @returns {FormData}
 */
export function buildBehaviourCsvUploadFormData(file) {
  const formData = new FormData()
  if (file) {
    formData.append("file", file)
  }
  return formData
}

/**
 * @typedef {Object} BehaviourCsvUploadRowResult
 * @property {number} row
 * @property {boolean} success
 * @property {string} [error]
 */

/**
 * @typedef {Object} BehaviourCsvUploadResult
 * @property {number} totalRows
 * @property {number} inserted
 * @property {number} failed
 * @property {BehaviourCsvUploadRowResult[]} rowResults
 * @property {string} status
 */

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {BehaviourCsvUploadResult}
 */
export function parseBehaviourCsvUploadResponse(body) {
  const data = body?.data ?? {}
  const rowResults = Array.isArray(data?.row_results) ? data.row_results : []

  return {
    totalRows: Number(data?.total_rows) || 0,
    inserted: Number(data?.inserted) || 0,
    failed: Number(data?.failed) || 0,
    rowResults,
    status: String(body?.status ?? "")
  }
}

/**
 * @param {unknown} err
 * @returns {string}
 */
export function parseBehaviourCsvUploadApiError(err) {
  const data = err?.response?.data
  return String(data?.error ?? data?.message ?? err?.message ?? "").trim() || ""
}

/**
 * @param {BehaviourCsvUploadRowResult[] | null | undefined} rowResults
 * @returns {string[]}
 */
export function getBehaviourCsvUploadFailedRowMessages(rowResults) {
  return (rowResults || [])
    .filter((row) => row?.success === false)
    .map((row) => {
      const rowNumber = Number(row?.row)
      const error = String(row?.error ?? "").trim() || "Unknown error"
      return Number.isFinite(rowNumber) ? `Row ${rowNumber}: ${error}` : error
    })
}

/**
 * @param {BehaviourCsvUploadResult} result
 * @returns {boolean}
 */
export function hasBehaviourCsvUploadInsertions(result) {
  return Number(result?.inserted) > 0
}

/**
 * @param {BehaviourCsvUploadResult} result
 * @returns {boolean}
 */
export function hasBehaviourCsvUploadFailures(result) {
  return Number(result?.failed) > 0
}
